/**
 * Reset Event Wizard
 * 3-step safety wizard before resetting the event:
 *   1) Archive ZIP (mandatory download)
 *   2) Choose what to clear
 *   3) Type APAGAR to confirm and execute
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Archive, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

import { API_URL } from "../../config";
import { archiveEventToZip } from "../../lib/archiveEvent";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

const DEFAULT_OPTS = {
  clear_registrations: true,
  clear_coupons: true,
  clear_emails: true,
  clear_visits: true,
  clear_groups: false,
  reset_sessions: false,
  reset_seats: true,
};

const FIELD_LABELS = {
  clear_registrations: { label: "Apagar vendas e transações", warn: "Inclui bilhetes e check-ins" },
  clear_coupons: { label: "Apagar cupões", warn: "Todos os cupões (incluindo grátis)" },
  clear_emails: { label: "Apagar histórico de comunicação", warn: "Campanhas e seus destinatários" },
  clear_visits: { label: "Apagar registos de visitas (analytics)", warn: "Tracking de visitantes" },
  clear_groups: { label: "Apagar grupos personalizados", warn: "Códigos de grupo (raro)" },
  reset_sessions: { label: "Apagar TODAS as sessões", warn: "⚠️ Apaga datas/horários. Vai precisar de recriar." },
  reset_seats: { label: "Repor lugares disponíveis das sessões existentes", warn: "(só usado se NÃO apagar sessões)" },
};

export const ResetEventWizard = ({ open, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [archiving, setArchiving] = useState(false);
  const [archiveStatus, setArchiveStatus] = useState("");
  const [archived, setArchived] = useState(false);
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const [confirmText, setConfirmText] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const resetWizard = () => {
    setStep(1); setArchiving(false); setArchiveStatus(""); setArchived(false);
    setOpts(DEFAULT_OPTS); setConfirmText(""); setAdminPassword(""); setExecuting(false); setResult(null);
  };

  const close = () => { resetWizard(); onClose(); };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      const name = await archiveEventToZip(setArchiveStatus);
      toast.success(`Arquivo "${name}.zip" descarregado`);
      setArchived(true);
    } catch (e) {
      toast.error("Erro ao gerar arquivo: " + (e?.message || ""));
    } finally {
      setArchiving(false);
      setArchiveStatus("");
    }
  };

  const handleExecute = async () => {
    if (confirmText !== "APAGAR") {
      toast.error("Escreva APAGAR exactamente (maiúsculas) para confirmar.");
      return;
    }
    if (!adminPassword) {
      toast.error("Insira a password de administrador.");
      return;
    }
    setExecuting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/reset-event`,
        { confirm: "APAGAR", password: adminPassword, ...opts },
        { withCredentials: true }
      );
      setResult(r.data.details);
      toast.success(r.data.message);
      onComplete && onComplete();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao executar reset");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reset-event-wizard">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Reset para Novo Evento
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Passo {result ? "✓" : step} de 3 · Esta operação é irreversível.
          </DialogDescription>
        </DialogHeader>

        {/* ──── STEP 1: ARCHIVE ──── */}
        {!result && step === 1 && (
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-sm text-amber-100">
              <strong>Antes de apagar nada</strong>, descarregue o arquivo ZIP com todas as informações do evento. Este passo é obrigatório.
            </div>
            <Button
              onClick={handleArchive}
              disabled={archiving}
              className="btn-primary w-full"
              data-testid="reset-step1-archive"
            >
              {archiving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
              {archiving ? archiveStatus || "A arquivar..." : archived ? "Arquivar de novo" : "Descarregar ZIP do evento actual"}
            </Button>
            {archived && (
              <div className="flex items-center gap-2 text-emerald-400 text-sm">
                <CheckCircle2 className="w-4 h-4" /> Arquivo descarregado. Pode prosseguir.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="border-white/10 text-zinc-300" onClick={close}>Cancelar</Button>
              <Button
                disabled={!archived}
                onClick={() => setStep(2)}
                className="btn-primary"
                data-testid="reset-step1-next"
              >
                Próximo
              </Button>
            </div>
          </div>
        )}

        {/* ──── STEP 2: OPTIONS ──── */}
        {!result && step === 2 && (
          <div className="space-y-3">
            <p className="text-zinc-400 text-sm">Escolha o que quer apagar. Configurações (página, técnica, tipos de bilhete, formulário, display) são <strong>sempre preservadas</strong>.</p>
            <div className="space-y-2 bg-zinc-950 border border-white/10 rounded-lg p-3">
              {Object.entries(FIELD_LABELS).map(([key, meta]) => (
                <div key={key} className="flex items-start gap-3 py-1.5">
                  <Switch
                    checked={opts[key]}
                    onCheckedChange={(v) => setOpts((p) => ({ ...p, [key]: v }))}
                    className="mt-1"
                    data-testid={`reset-opt-${key}`}
                  />
                  <div className="flex-1">
                    <p className="text-white text-sm">{meta.label}</p>
                    <p className="text-zinc-500 text-xs">{meta.warn}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" className="border-white/10 text-zinc-300" onClick={() => setStep(1)}>Voltar</Button>
              <Button onClick={() => setStep(3)} className="btn-primary" data-testid="reset-step2-next">Próximo</Button>
            </div>
          </div>
        )}

        {/* ──── STEP 3: CONFIRM ──── */}
        {!result && step === 3 && (
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-sm text-red-100">
              <p className="font-semibold mb-2">⚠️ Atenção: esta acção é IRREVERSÍVEL.</p>
              <p>Vai apagar permanentemente:</p>
              <ul className="list-disc list-inside mt-2 space-y-0.5">
                {Object.entries(opts).filter(([_, v]) => v).map(([k]) => (
                  <li key={k}>{FIELD_LABELS[k].label}</li>
                ))}
              </ul>
            </div>
            <div>
              <Label className="text-zinc-300">Escreva <strong className="text-red-400 font-mono">APAGAR</strong> para confirmar</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="APAGAR"
                className="input-dark mt-2 font-mono"
                data-testid="reset-confirm-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Password de administrador</Label>
              <Input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="••••••••"
                className="input-dark mt-2"
                data-testid="reset-password-input"
                autoComplete="current-password"
              />
              <p className="text-zinc-500 text-xs mt-1">Para sua segurança, confirme a sua password.</p>
            </div>
            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" className="border-white/10 text-zinc-300" onClick={() => setStep(2)}>Voltar</Button>
              <Button
                onClick={handleExecute}
                disabled={executing || confirmText !== "APAGAR" || !adminPassword}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="reset-execute"
              >
                {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {executing ? "A apagar..." : "Apagar definitivamente"}
              </Button>
            </div>
          </div>
        )}

        {/* ──── RESULT ──── */}
        {result && (
          <div className="space-y-3">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-sm text-emerald-100">
              <p className="font-semibold flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5" /> Reset concluído com sucesso.
              </p>
              <p>A página vai recarregar dentro de momentos.</p>
            </div>
            <div className="bg-zinc-950 border border-white/10 rounded-lg p-3 text-xs">
              <p className="text-zinc-400 mb-2 font-semibold">Resumo:</p>
              <pre className="text-zinc-300 whitespace-pre-wrap break-all">{JSON.stringify(result, null, 2)}</pre>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={() => { close(); window.location.reload(); }} className="btn-primary">Fechar e recarregar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
